/**
 * bakeLightingApi — the single seam between the client and the server bake
 * (ENG-561 environment IBL maps).
 *
 * The asset-manager owns the environment asset and exposes, on it:
 *   bakeStatus:   "none" | "baking" | "ready" | "error"
 *   bakeProgress: 0..100
 * and an action endpoint:
 *   POST /api/environment-assets/{id}/bake  -> flips to baking, returns the asset
 * (the asset-manager calls the lightmap baking service in the background; the
 * maps land beside the .ply under <assetFolder>/baked/).
 *
 * STUB=true fakes the lifecycle locally so the UX runs before the asset-manager
 * bake endpoint is deployed. Flip to false once it's live (local stack: AAM with
 * BAKE_SERVICE_URL set + the bake service running).
 */
import { listOrgEnvironments, bakeOrgEnvironment } from './assetManagerApi.js'
import { bakeKindFlags } from './bakeKind.js'
import { isOrgEnvironmentsEnabled } from './featureFlags.js'

export const STUB = false

// The asset id the bake endpoints are keyed on is CARRIED, not derived: see
// activeEnvAssetId (src/lib/activeEnvironmentAsset.js), which reads the id the
// server stated. There used to be an envAssetIdFromUrl here that recovered it by
// regex from the splat URL; it returned nothing for org-layout URLs, and a
// second storage layout is exactly the kind of thing a client should never have
// been able to notice (ENG-999).

// Format the bake can handle (rasterizer is INRIA .ply only).
export function isBakeableUrl(url) {
  const path = (url || '').split('?')[0].toLowerCase()
  return path.endsWith('.ply')
}

// ---- stub state (module-local; one entry per assetId) --------------------
const _stub = new Map()
function _stubGet(id) {
  if (!_stub.has(id)) _stub.set(id, { status: 'none', progress: 0, error: null })
  return _stub.get(id)
}

/**
 * Read the current bake status for an environment asset. progress is 0..1.
 * Queries the org environment bucket; no slug needed since all environments
 * are org-scoped (ENG-561 bake UX).
 * Returns name so callers can track the asset and show a meaningful completion toast.
 */
export async function fetchBakeStatus(assetId) {
  if (!isOrgEnvironmentsEnabled()) {
    // Flag OFF: org environments feature dormant, no API call
    return { status: 'none', progress: 0, error: null, name: '' }
  }
  if (STUB) {
    const s = _stubGet(assetId)
    return { status: s.status, progress: s.progress, error: s.error }
  }
  // The asset-manager has no GET-by-id for environments — read the org env list and
  // find this asset.
  const list = await listOrgEnvironments()
  const a = (list || []).find(e => e.id === assetId)
  if (!a) throw new Error(`environment asset ${assetId} not found`)
  return {
    status: a.bakeStatus || 'none',
    progress: (a.bakeProgress ?? 0) / 100, // current phase's 0..1 (ENG-592)
    phase: a.bakePhase || '',
    stage: a.bakeStage ?? 0,
    stages: a.bakeStages ?? 0,
    error: null,
    name: a.name,
  }
}

/**
 * Trigger a bake with the chosen parameters. Returns the new status.
 * `params` = { lpSpacing, lpLayers, reflSpacing, reflInset }.
 */
export async function requestBake(assetId, params = {}) {
  if (!isOrgEnvironmentsEnabled()) {
    // Flag OFF: org environments feature dormant, no bake request
    throw new Error('org environments feature not enabled')
  }
  if (STUB) {
    const s = _stubGet(assetId)
    s.status = 'baking'; s.progress = 0; s.error = null
    return { status: 'baking', progress: 0 }
  }
  const a = await bakeOrgEnvironment(assetId, {
    // ENG-592: lighting-only — don't re-run the navmesh carve on a lighting bake.
    ...bakeKindFlags('lighting'),
    light_probe_spacing: params.lpSpacing,
    light_probe_layers: params.lpLayers,
    reflection_spacing: params.reflSpacing,
    reflection_inset: params.reflInset,
  })
  return { status: a.bakeStatus || 'baking', progress: (a.bakeProgress ?? 0) / 100 }
}

/**
 * Request a NAVMESH build (ENG-591) — separate from the lighting bake (mirrors
 * UE's separate Build Lighting / Build Paths). Sends the character capsule so the
 * server carve uses the right agent size. Backend support is the deferred
 * software-WebGPU stage; until then this surfaces a loud error (no fake success).
 */
export async function requestNavBake(assetId, params = {}) {
  if (!isOrgEnvironmentsEnabled()) {
    // Flag OFF: org environments feature dormant, no bake request
    throw new Error('org environments feature not enabled')
  }
  if (STUB) return { status: 'baking', progress: 0 }
  const a = await bakeOrgEnvironment(assetId, {
    // ENG-592: navmesh-only — skip the slow reflection captures (~1s vs minutes).
    ...bakeKindFlags('navmesh'),
    agent_radius: params.agentRadius,
    agent_height: params.agentHeight,
    navmesh_cell: params.navCell,
  })
  return { status: a.bakeStatus || 'baking', progress: (a.bakeProgress ?? 0) / 100 }
}

/** STUB ONLY: advance a fake baking job one tick. Called by the store poller. */
export function _stubAdvance(assetId) {
  if (!STUB) return
  const s = _stubGet(assetId)
  if (s.status === 'baking') {
    s.progress = Math.min(1, (s.progress || 0) + 0.12)
    if (s.progress >= 1) s.status = 'ready'
  }
}
